import { query, where } from "firebase/firestore";
import { normalizeSampleAgendaContentBox } from "utils/productTour";
import {
  getDateKeyFromInput,
  isDateKeyInRange,
  parseDateKeyToLocalDate,
} from "utils/dateTime";

const DAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const getBoxId = (box) => String(box?.contentBoxId ?? box?.id ?? "");

const parseLocalDateKey = (dateKey) => {
  return parseDateKeyToLocalDate(dateKey);
};

const getDayNamePlural = (date) => `${DAYS_LONG[date.getDay()]}s`;

export const getRecurringActiveDateDayNamesForScope = (scope = {}) => {
  const startDate = parseLocalDateKey(scope.dateKey);
  if (!startDate) return [];

  const endDate = scope.weekEndKey ? parseLocalDateKey(scope.weekEndKey) : startDate;
  const resolvedEndDate = endDate && endDate >= startDate ? endDate : startDate;
  const dayNames = new Set();
  const cursor = new Date(startDate);

  while (cursor <= resolvedEndDate && dayNames.size < DAYS_LONG.length) {
    dayNames.add(getDayNamePlural(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return Array.from(dayNames);
};

export const shouldSuppressContentBoxSnapshot = (snapshot, classId) => {
  const shiftGate = typeof window !== "undefined" ? window.__shiftSnapshotGate : null;
  return (
    shiftGate?.enabled === true
    && Array.isArray(shiftGate.classIds)
    && shiftGate.classIds.includes(classId)
    && snapshot.metadata?.hasPendingWrites === true
  );
};

// Snapshot metadata (fromCache, hasPendingWrites) is consumed via
// utils/firestoreSnapshotMetadata.js and onSnapshotWithMetadata in userSlice.

export const hydrateContentBoxFromSnapshot = (docSnap) => {
  const data = typeof docSnap?.data === "function" ? (docSnap.data() || {}) : {};
  const documentId = String(docSnap?.id || "");
  return {
    ...data,
    // Always trust the Firestore document id. Template copies can leave a
    // leftover `id` (and rarely a drifted contentBoxId) that would otherwise
    // win when callers spread `data()` after `id: docSnap.id`.
    id: documentId,
    contentBoxId: documentId,
  };
};

export const mapSnapshotToContentBoxes = (snapshot) =>
  (snapshot?.docs || []).map((docSnap) =>
    normalizeSampleAgendaContentBox(hydrateContentBoxFromSnapshot(docSnap))
  );

export const mergeContentBoxDocuments = (documentGroups = []) => {
  const mergedById = new Map();

  documentGroups.forEach((documents) => {
    (documents || []).forEach((box) => {
      const boxId = getBoxId(box);
      if (!boxId) return;
      mergedById.set(boxId, normalizeSampleAgendaContentBox({
        ...mergedById.get(boxId),
        ...box,
        id: boxId,
        contentBoxId: boxId,
      }));
    });
  });

  return Array.from(mergedById.values());
};

export const buildContentBoxScopeKey = (scope = {}) => {
  const {
    mode = "presentDay",
    dateKey = "",
    weekEndKey = "",
    useActiveDates = false,
  } = scope;

  return `${mode}:${dateKey}:${weekEndKey}:${useActiveDates ? 1 : 0}`;
};

/**
 * Build Firestore queries for Present/Share/Calendar scoped listeners.
 * scope: { useActiveDates, mode, dateKey, weekEndKey? }
 *
 * When Active Dates are off (Whiteboard Mode), load the full class collection.
 * Present still filters to active boxes client-side; the storage modal needs
 * inactive boxes in Redux so "open storage" matches Content Manager.
 */
export const buildContentBoxPresentQueries = (contentBoxesRef, scope = {}) => {
  const { useActiveDates, dateKey, includeAllAgendas = true } = scope;

  if (!useActiveDates) {
    return [query(contentBoxesRef)];
  }

  if (!dateKey) {
    return [query(contentBoxesRef)];
  }

  const activeDateQueries = [
    query(contentBoxesRef, where("endDate", ">=", dateKey)),
    query(contentBoxesRef, where("startDate", "==", "")),
  ];
  if (includeAllAgendas) {
    activeDateQueries.push(query(contentBoxesRef, where("agendaBox", "==", true)));
  }

  const recurringDayNames = getRecurringActiveDateDayNamesForScope(scope);
  if (recurringDayNames.length === 1) {
    activeDateQueries.push(
      query(contentBoxesRef, where("startDate", "array-contains", recurringDayNames[0]))
    );
  } else if (recurringDayNames.length > 1) {
    activeDateQueries.push(
      query(contentBoxesRef, where("startDate", "array-contains-any", recurringDayNames))
    );
  }

  return activeDateQueries;
};

export const filterContentBoxesForPresentDay = (boxes, date, settings = {}) => {
  if (!Array.isArray(boxes)) return [];

  if (!settings?.useActiveDates) {
    return boxes.filter((box) => box.active === true);
  }

  return boxes.filter((box) => {
    if (!box.startDate) return true;
    if (Array.isArray(box.startDate)) {
      const dayNamePlural = date?.dayNamePlural || (date?.dayName ? `${date.dayName}s` : "");
      return box.startDate.includes(dayNamePlural);
    }

    return isDateKeyInRange(
      getDateKeyFromInput(date),
      box.startDate,
      box.endDate || box.startDate
    );
  });
};
